"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SiwsMessage = void 0;
const utils_1 = require("./utils");
class SiwsMessage {
    /**RFC 4501 dns authority that is requesting the signing. */
    domain;
    /**Substrate address signing the message. */
    address;
    /**Azero ID */
    azeroId;
    /**Human-readable ASCII assertion that the user will sign, and it must not contain `\n`. */
    statement;
    /**RFC 3986 URI referring to the resource that is the subject of the signing. */
    uri;
    /**Randomized token used to prevent replay attacks. */
    nonce;
    /**Will appear as `sign in with your {{chainName}} account:` */
    chainName;
    /**Identifier for chain-specific applications */
    chainId;
    /**timestamp that indicates when the signed authentication message is no longer valid. */
    expirationTime;
    /**timestamp of the current time. */
    issuedAt;
    constructor(param) {
        this.domain = param.domain;
        this.address = param.address;
        this.azeroId = param.azeroId;
        this.statement = param.statement;
        this.uri = param.uri;
        this.nonce = param.nonce;
        this.chainId = param.chainId;
        this.chainName = param.chainName;
        this.expirationTime = param.expirationTime;
        this.issuedAt = param.issuedAt;
        this.validateMessage();
    }
    /**
     * Returns the message as JSON object, useful for when you need to store the payload without any methods.
     */
    get asJson() {
        return {
            domain: this.domain,
            address: this.address,
            azeroId: this.azeroId,
            statement: this.statement,
            uri: this.uri,
            nonce: this.nonce,
            chainId: this.chainId,
            issuedAt: this.issuedAt,
            expirationTime: this.expirationTime,
        };
    }
    /**
     * Prepares the message in stringified JSON format.
     */
    prepareJson() {
        this.validateMessage();
        this.issuedAt = this.issuedAt ?? new Date().getTime();
        return JSON.stringify(this.asJson, undefined, 2);
    }
    /**
     * Prepares the message to be signed in human readable format.
     */
    prepareMessage() {
        this.validateMessage();
        let message = `${this.domain} wants you to sign in with your ${this.chainName ?? "Substrate"} account:\n`;
        message += `${this.address}\n`;
        if (this.azeroId)
            message += `(${this.azeroId})\n`;
        message += "\n";
        if (this.statement)
            message += `${this.statement}\n\n`;
        const uriField = `URI: ${this.uri}`;
        const body = [uriField];
        if (this.chainId)
            body.push(`Chain ID: ${this.chainId}`);
        body.push(`Nonce: ${this.nonce}`);
        this.issuedAt = this.issuedAt ?? new Date().getTime();
        body.push(`Issued At: ${new Date(this.issuedAt).toISOString()}`);
        if (this.expirationTime)
            body.push(`Expiration Time: ${new Date(this.expirationTime).toISOString()}`);
        message += body.join("\n");
        return message;
    }
    /**
     * Utility function that wraps @polkadotjs api.
     * @param source You can get this from `web3FromSource(injectedAccount.meta.source)`
     * */
    async sign(injectedExtension) {
        if (!injectedExtension.signer.signRaw)
            throw new Error("Wallet does not support signing message.");
        const message = this.prepareMessage();
        const { signature } = await injectedExtension.signer.signRaw({
            address: this.address,
            data: message,
            type: "payload",
        });
        return { signature, message };
    }
    /**
     * Utility function that wraps @polkadotjs api.
     * @param source You can get this from `web3FromSource(injectedAccount.meta.source)`
     * */
    async signJson(injectedExtension) {
        if (!injectedExtension.signer.signRaw)
            throw new Error("Wallet does not support signing message.");
        const message = this.prepareJson();
        const { signature } = await injectedExtension.signer.signRaw({
            address: this.address,
            data: message,
            type: "payload",
        });
        return { signature, message };
    }
    validateMessage() {
        if (!this.domain || this.domain.length === 0)
            throw new Error("SIWS Error: domain is required");
        // invalid SS58 address
        if (!utils_1.Address.fromSs58(this.address))
            throw new Error("SIWS Error: address is not a valid substrate address");
        // uri is required for wallets validation and to help prevent phishing attacks
        if (!this.uri || this.uri.length === 0)
            throw new Error("SIWS Error: uri is required");
        // nonce is required
        if (!this.nonce || this.nonce.length === 0)
            throw new Error("SIWS Error: nonce is required");
        if (this.issuedAt) {
            const issuedAtDate = new Date(this.issuedAt);
            // invalid timestamp
            if (isNaN(issuedAtDate.getTime()))
                throw new Error("SIWS Error: issuedAt is not a valid date");
        }
        if (this.expirationTime) {
            const expirationTimeDate = new Date(this.expirationTime);
            // invalid timestamp
            if (isNaN(expirationTimeDate.getTime()))
                throw new Error("SIWS Error: expirationTime is not a valid date");
            // cannot expire before issuedAt
            if (this.issuedAt && expirationTimeDate.getTime() <= new Date(this.issuedAt).getTime())
                throw new Error("SIWS Error: expirationTime must be greater than issuedAt");
            // token has expired
            if (expirationTimeDate.getTime() <= new Date().getTime())
                throw new Error("SIWS Error: message has expired!");
        }
    }
    async verifyAzeroId() {
        if (!this.azeroId)
            return true;
        try {
            const { resolveDomainToAddress } = await Promise.resolve().then(() => __importStar(require("@azns/resolver-core")));
            const { address, error } = await resolveDomainToAddress(this.azeroId);
            if (!address) {
                if (error)
                    console.error(error);
                return false;
            }
            const parsedAddress = utils_1.Address.fromSs58(this.address);
            const resolvedAddress = utils_1.Address.fromSs58(address);
            return !!parsedAddress && !!resolvedAddress && parsedAddress.isEqual(resolvedAddress);
        }
        catch (e) {
            console.error(e);
            return false;
        }
    }
}
exports.SiwsMessage = SiwsMessage;
